import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { ProductCategory } from '../../types';
import { productService } from '../../services/productService';
import './ProductCategoryDetailPage.css';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';

const ProductCategoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [parentCategory, setParentCategory] = useState<ProductCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCategory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productService.getCategoryById(parseInt(id || '0'));
      if (response.data.data) {
        const cat = response.data.data as ProductCategory;
        setCategory(cat);

        // Load parent category if exists
        if (cat.parentCategoryId) {
          const parentRes = await productService.getCategoryById(
            cat.parentCategoryId
          );
          if (parentRes.data.data) {
            setParentCategory(parentRes.data.data as ProductCategory);
          }
        }
      }
      setLoading(false);
    } catch (err) {
      setError(t('productCategory.failedToLoadCategory'));
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!category) return;
    navigate(`/products/categories/${category.id}/delete`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!category) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{t('productCategory.categoryNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="detail-container">
        {/* Navigation Breadcrumb */}
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/products/categories')}
            title={t('productCategory.backToCategories')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('productCategory.title')}
          </button>
        </nav>

        {/* Page Title */}
        <div className="detail-header">
          <h1 className="detail-title">{category.name}</h1>
          <p className="detail-subtitle">{t('productCategory.viewTitle')}</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="detail-content">
          {/* Details Section */}
          <div className="detail-main-section">
            {/* Category Details Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('productCategory.categoryDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('productCategory.categoryName')}</label>
                    <p className="detail-value">{category.name}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('productCategory.categoryType')}</label>
                    <div className="detail-value">
                      {category.parentCategoryId ? (
                        <span className="info-badge info-badge-sub">{t('productCategory.subCategory')}</span>
                      ) : (
                        <span className="info-badge info-badge-root">{t('productCategory.rootCategory')}</span>
                      )}
                    </div>
                  </div>

                  {category.parentCategoryId && parentCategory && (
                    <div className="detail-field detail-field-full">
                      <label className="detail-label">{t('productCategory.parentCategory')}</label>
                      <div className="detail-value">
                        <span className="detail-parent-badge">{parentCategory.name}</span>
                      </div>
                    </div>
                  )}

                  <div className="detail-field detail-field-full">
                    <label className="detail-label">{t('productCategory.description')}</label>
                    <p className="detail-value detail-description">
                      {category.description || (
                        <em className="detail-empty">{t('productCategory.noDescriptionProvided')}</em>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Information Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('productCategory.information')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('productCategory.categoryId')}</label>
                    <p className="detail-value detail-code">{category.id}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('productCategory.status')}</label>
                    <div className="detail-value">
                      <span className="info-badge info-badge-active">{t('productCategory.active')}</span>
                    </div>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('productCategory.createdDate')}</label>
                    <p className="detail-value">
                      {new Date(category.createdAt).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US')}
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('productCategory.createdTime')}</label>
                    <p className="detail-value">
                      {new Date(category.createdAt).toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : 'en-US')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="detail-sidebar">
            {/* Actions Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('productCategory.actions')}</h3>
              </div>
              <div className="detail-card-body detail-actions">
                <button
                  className="btn-save"
                  onClick={() =>
                    navigate(`/products/categories/${category.id}/edit`)
                  }
                  title={t('productCategory.editCategory')}
                >
                  <PenLine size={16} strokeWidth={2} />
                  {t('productCategory.editCategory')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  title={t('productCategory.deleteCategory')}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  {t('productCategory.deleteCategory')}
                </button>

                <div className="detail-divider"></div>

                <button
                  className="detail-btn detail-btn-secondary"
                  onClick={() => navigate('/products/categories')}
                  title={t('productCategory.backToCategories')}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  {t('productCategory.backToCategories')}
                </button>
              </div>
            </div>

            {/* Quick Links Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('productCategory.quickActions')}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/products/categories/${category.id}/edit`)}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('productCategory.editCategory')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/products')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('productCategory.viewProducts')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/products/categories/create')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('productCategory.createSubCategory')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductCategoryDetailPage;
